export type WidgetPosition = "bottom-right" | "bottom-left";
export type WidgetTheme = "AUTO" | "light" | "dark";

export interface ResolvedWidgetConfig {
  agentId: string;
  apiBase: string;
  defaultOpen: boolean;
  position: WidgetPosition;
  theme: WidgetTheme;
  suggestions: string[];
  salutations: string[];
  initialMessages: string[];
  showRecentChats: boolean;
  showBranding: boolean;
}

const env = (import.meta as ImportMeta & {
  env: { VITE_API_BASE_URL?: string };
}).env;

const envApiBase = env.VITE_API_BASE_URL?.trim() ?? "";

const DEFAULTS: Omit<ResolvedWidgetConfig, "agentId"> = {
  apiBase: envApiBase,
  defaultOpen: false,
  position: "bottom-right",
  theme: "AUTO",
  suggestions: [],
  showRecentChats: true,
  showBranding: true,
  salutations: [],
  initialMessages: [],
};

function parseBool(v: string | null | undefined, fallback: boolean): boolean {
  if (v == null) return fallback;
  const s = v.trim().toLowerCase();
  if (["true", "1", "yes", "y"].includes(s)) return true;
  if (["false", "0", "no", "n"].includes(s)) return false;
  return fallback;
}

function parseEnum<T extends string>(
  v: string | null | undefined,
  allowed: readonly T[],
  fallback: T,
): T {
  if (!v) return fallback;
  const s = v.trim() as T;
  return (allowed as readonly string[]).includes(s) ? s : fallback;
}

function parseSuggestions(v: string | null | undefined): string[] {
  if (!v) return [];
  return v
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 8); // keep it sane for UI
}

function parseInitialMessages(v: string | null | undefined): string[] {
  if (!v) return [];
  return v
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 8); // keep it sane for UI
}

function parseSalutations(v: string | null | undefined): string[] {
  if (!v) {
    return [
      "👋 Hi! How can I help you today?",
      "😊 Welcome! What would you like to know?",
      "🚀 Need help? Ask me anything!",
      "🤖 I’m here to help.",
    ];
  }

  try {
    const parsed = JSON.parse(v);
    return Array.isArray(parsed) ? parsed.map(String).slice(0, 8) : [];
  } catch {
    console.warn("[ChatGrow] Invalid salutations JSON:", v);
    return [];
  }
}

export function resolveConfig(el: HTMLElement): ResolvedWidgetConfig {
  const agentId = el.getAttribute("agent-id")?.trim();
  if (!agentId) throw new Error("Missing required attribute: agent-id");

  const apiBase = el.getAttribute("api-base")?.trim() || DEFAULTS.apiBase;
  if (!apiBase) throw new Error("Missing API base URL: set api-base or VITE_API_BASE_URL");

  return {
    agentId,
    apiBase,
    defaultOpen: parseBool(
      el.getAttribute("default-open"),
      DEFAULTS.defaultOpen,
    ),
    position: parseEnum(
      el.getAttribute("position"),
      ["bottom-right", "bottom-left"] as const,
      DEFAULTS.position,
    ),
    theme: parseEnum(
      el.getAttribute("theme"),
      ["AUTO", "light", "dark"] as const,
      DEFAULTS.theme,
    ),
    suggestions: parseSuggestions(el.getAttribute("suggestions")),
    salutations: parseSalutations(el.getAttribute("salutations")),
    initialMessages: parseInitialMessages(el.getAttribute("initial-messages")),
    showRecentChats: parseBool(
      el.getAttribute("show-recent-chats"),
      DEFAULTS.showRecentChats,
    ),
    showBranding: parseBool(
      el.getAttribute("show-branding"),
      DEFAULTS.showBranding,
    ),
  };
}
